import { FC, useMemo } from "react";
import { ChevronRightIcon } from "lucide-react-native";

import {
  ApplicationTypeEnumType,
  FederalGroupEnumType,
} from "@libs/api/patients";
import {
  NewQuickApplyButton,
  StartApplicationButton,
} from "@/blocks/DashboardApplicationActionButtons";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { ListedApplicationType } from "@/domain/applications";
import { getApplicationTypeColor } from "@/utils/applications";
import { composeBrandLabel, getProgramTypeLabel } from "@/utils/brandLabel";
import { cn } from "@/utils/tailwind";

type ApplicationTypeCardPropsType = {
  title: string;
  applicationType: ApplicationTypeEnumType;
};

const ApplicationTypeCard: FC<ApplicationTypeCardPropsType> = ({
  title,
  applicationType,
}) => (
  <VnLink
    href={{
      pathname: "/(authed)/application/[applicationType]",
      params: { applicationType },
    }}>
    <VnCard className="relative flex flex-row items-center justify-between overflow-hidden">
      <VnBox
        className={cn(
          "absolute left-0 size-3 h-full",
          getApplicationTypeColor(applicationType),
        )}
      />
      <VnText variant="lgMedium" className="ml-2">
        {title}
      </VnText>
      <VnIcon as={ChevronRightIcon} />
    </VnCard>
  </VnLink>
);

type ApplicationsSectionPropsType = {
  submittedApplications?: ListedApplicationType[];
  hasDraftApplications: boolean;
};

/**
 * Persistent "Applications" section listing submitted applications by type.
 * Presentational: data is passed in so a DEV scenario override drives it too.
 * The actionable begin/continue CTA lives in the "Next steps" feed, not here.
 */
const ApplicationsSection: FC<ApplicationsSectionPropsType> = ({
  submittedApplications,
  hasDraftApplications,
}) => {
  // Group submitted applications by type and compute brand labels.
  const submittedByType = useMemo(() => {
    const grouped: Partial<
      Record<
        ApplicationTypeEnumType,
        {
          brands: {
            customUsStateName: string | null;
            federalGroup: FederalGroupEnumType;
          }[];
        }
      >
    > = {};

    if (!submittedApplications?.length) return grouped;

    for (const app of submittedApplications) {
      grouped[app.applicationType] ??= { brands: [] };
      grouped[app.applicationType]!.brands.push(...app.brands);
    }

    return grouped;
  }, [submittedApplications]);

  if (!submittedApplications?.length) {
    return null;
  }

  return (
    <VnBox className="gap-6">
      <VnBox className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
        <VnText
          variant="headerTwo"
          i18nKey="native:applicationSection.applications"
        />

        <VnBox className="flex flex-row items-center gap-4">
          <VnBox className="flex-1 md:flex-none">
            {hasDraftApplications ? (
              <NewQuickApplyButton
                variant="secondary"
                className="w-full md:w-auto"
              />
            ) : (
              <StartApplicationButton
                variant="secondary"
                className="w-full md:w-auto"
                i18nKey="native:applicationSection.quickApply"
              />
            )}
          </VnBox>
          {/* Empty cell keeps the button half-width on mobile. */}
          <VnBox className="flex-1 md:hidden" />
        </VnBox>
      </VnBox>

      <VnBox className="flex w-full flex-col gap-5">
        {submittedByType.MEDICAID && (
          <ApplicationTypeCard
            title={composeBrandLabel(
              submittedByType.MEDICAID.brands,
              "MEDICAID",
            )}
            applicationType="MEDICAID"
          />
        )}
        {submittedByType.MEDICARE && (
          <ApplicationTypeCard
            title={getProgramTypeLabel("MEDICARE")}
            applicationType="MEDICARE"
          />
        )}
        {submittedByType.MARKETPLACE && (
          <ApplicationTypeCard
            title={composeBrandLabel(
              submittedByType.MARKETPLACE.brands,
              "MARKETPLACE",
            )}
            applicationType="MARKETPLACE"
          />
        )}
      </VnBox>
    </VnBox>
  );
};

export { ApplicationsSection };
